import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthContext";
import { CompanyPanel } from "@/components/customers/CompanyPanel";
import { CustomerCallStrip } from "@/components/customers/CustomerCallStrip";
import {
  CustomerEditDrawer,
  type CustomerDrawerState,
} from "@/components/customers/CustomerEditDrawer";
import { CustomerPanel } from "@/components/customers/CustomerPanel";
import { customerName, initialsOf } from "@/components/customers/customerFormat";
import {
  useCustomerShortlist,
  useFavoriteToggle,
  type FavoriteCustomer,
} from "@/components/customers/favorites";
import { VCARD_EXPORT_MAX } from "@/components/customers/vcardExport";
import { DropOverlay } from "@/components/customers/AvatarEditor";
import { VcardImportDialog } from "@/components/customers/VcardImport";
import { dropRejectMessage } from "@/components/customers/dropMessage";
import { Avatar } from "@/components/ui/Avatar";
import { Spinner } from "@/components/ui/Spinner";
import {
  AddressBookIcon,
  BuildingIcon,
  DownloadIcon,
  PlusIcon,
  SearchIcon,
  StarIcon,
  UploadIcon,
} from "@/components/ui/icons";
import { api, type CustomerFavoriteEntry, type CustomerShortlistEntry } from "@/lib/api";
import { domainFromUrl } from "@/lib/avatar";
import { cn } from "@/lib/cn";
import { displayTimeZone } from "@/lib/timeZone";
import { isVcardFile, useFileDrop } from "@/lib/useFileDrop";

const PAGE_SIZE = 30;

export type CustomerDirectorySearch = {
  q?: string;
  /** Left-rail tab; absent = people. A search term searches all three. */
  tab?: "favorites" | "companies";
  /** Selected customer user (login). */
  sel?: string;
  /** Selected company (customer_id), companies tab. */
  company?: string;
  invalid?: boolean;
};

function useDebounced<T>(value: T, ms: number): T {
  const [out, setOut] = useState(value);
  useEffect(() => {
    const h = window.setTimeout(() => setOut(value), ms);
    return () => window.clearTimeout(h);
  }, [value, ms]);
  return out;
}

/** "10:42" today, "04.10." earlier — for the "Zuletzt" list. */
function shortWhen(iso: string, locale: string): string {
  const d = new Date(iso);
  const timeZone = displayTimeZone();
  const day = (x: Date) => x.toLocaleDateString(locale, { timeZone });
  if (day(d) === day(new Date())) {
    return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", timeZone });
  }
  return d.toLocaleDateString(locale, { day: "2-digit", month: "2-digit", timeZone });
}

const tabClass = (on: boolean) =>
  cn(
    "inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium transition-colors duration-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent",
    on ? "bg-surface-subtle text-ink shadow-sm" : "text-muted hover:text-ink",
  );

type RailTab = "favorites" | "people" | "companies";

/** Does a favorite match the typed term? Favorites are few and already loaded. */
function favoriteMatches(f: CustomerFavoriteEntry, term: string): boolean {
  const needle = term.toLowerCase();
  const digits = term.replace(/[^\d+]/g, "");
  const text = [f.first_name, f.last_name, f.email, f.login, f.company_name, f.customer_id]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (text.includes(needle)) return true;
  if (digits.length < 3) return false;
  return [f.phone, f.mobile].some((n) => (n ?? "").replace(/[^\d+]/g, "").includes(digits));
}

/**
 * Agent customer workbench ("Kunden"): on the left the agent's favorites,
 * people (recent and frequent) or companies — a typed term searches all
 * three at once; the selected customer or company on the right with its
 * contact card and tickets. A ringing call shows on top. Needs the
 * customer_directory feature; creating and editing needs customer_edit.
 */
export function CustomerDirectoryPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate({ from: "/agent/customers" });
  const search = useSearch({ from: "/agent/customers" });
  const allowed = Boolean(user?.can_use_customer_directory);
  const canEdit = Boolean(user?.can_edit_customers);
  const tab: RailTab =
    search.tab === "companies" ? "companies" : search.tab === "favorites" ? "favorites" : "people";

  const [query, setQuery] = useState(search.q ?? "");
  const debouncedQuery = useDebounced(query.trim(), 250);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [drawer, setDrawer] = useState<CustomerDrawerState | null>(null);
  // A .vcf (Apple Contacts drag, or picked) under review in the import dialog.
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const drop = useFileDrop({
    accept: isVcardFile,
    onFiles: ([file]) => {
      setImportError(null);
      setImportFile(file);
    },
    onReject: (r) => setImportError(dropRejectMessage(t, r, "vcard.wrongFile")),
    disabled: !canEdit,
    vcardText: true,
  });

  const setSearch = (patch: Partial<CustomerDirectorySearch>, replace = false) =>
    void navigate({
      search: (prev: CustomerDirectorySearch) => ({ ...prev, ...patch }),
      replace,
    });

  // Typing writes the (debounced) term to the URL.
  useEffect(() => {
    if ((search.q ?? "") === debouncedQuery) return;
    setPageSize(PAGE_SIZE);
    setSearch({ q: debouncedQuery || undefined }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setSearch is a fresh closure each render
  }, [debouncedQuery, search.q]);

  const q = search.q ?? "";
  const valid = search.invalid ? ("all" as const) : ("valid" as const);

  const shortlistQ = useCustomerShortlist(allowed);
  const peopleQ = useQuery({
    queryKey: ["customer-directory", "search", q, valid, pageSize],
    queryFn: ({ signal }) =>
      api.listCustomerDirectory({ search: q, valid, page: 1, pageSize }, signal),
    enabled: allowed && q !== "",
    placeholderData: keepPreviousData,
  });
  const companiesQ = useQuery({
    queryKey: ["customer-directory", "companies", q],
    queryFn: ({ signal }) => api.searchCustomerDirectoryCompanies(q, signal),
    enabled: allowed && q !== "",
  });

  const favorites = useMemo(() => shortlistQ.data?.favorites ?? [], [shortlistQ.data]);
  const recent = useMemo(() => shortlistQ.data?.recent ?? [], [shortlistQ.data]);
  const frequent = useMemo(() => shortlistQ.data?.frequent ?? [], [shortlistQ.data]);
  // Companies of the agent's own customers, for the companies tab without a term.
  const ownCompanies = useMemo(() => {
    const seen = new Map<string, { name: string; url: string | null }>();
    for (const e of [...favorites, ...recent, ...frequent]) {
      if (e.company_name && !seen.has(e.customer_id)) {
        seen.set(e.customer_id, { name: e.company_name, url: e.company_url ?? null });
      }
    }
    return Array.from(seen, ([customer_id, co]) => ({ customer_id, ...co }));
  }, [favorites, recent, frequent]);

  if (!allowed) {
    return (
      <div
        className="m-6 rounded-lg border border-hairline bg-surface p-8 text-center"
        data-testid="customer-directory-denied"
      >
        <h1 className="font-display text-lg font-semibold text-ink">
          {t("customerDirectory.deniedTitle")}
        </h1>
        <p className="mt-2 text-sm text-muted">{t("customerDirectory.deniedBody")}</p>
      </div>
    );
  }

  const selectPerson = (login: string) => setSearch({ sel: login, company: undefined });
  const selectCompany = (customerId: string) => setSearch({ company: customerId, sel: undefined });
  const openCompany = (customerId: string) =>
    setSearch({ tab: "companies", company: customerId, sel: undefined });
  const pickTab = (next: RailTab) => {
    setQuery("");
    setSearch({ tab: next === "people" ? undefined : next, q: undefined });
  };
  const favoriteLogins = new Set(favorites.map((f) => f.login));
  const favoriteHits = q ? favorites.filter((f) => favoriteMatches(f, q)) : [];
  // Favorites already head the hits; the directory list shows everyone else.
  const people = (peopleQ.data?.items ?? []).filter((p) => !favoriteLogins.has(p.login));
  const peopleTotal = peopleQ.data?.total ?? 0;
  const companies = companiesQ.data ?? [];
  const searching = q !== "" && (peopleQ.isLoading || companiesQ.isLoading);
  const noHits =
    q !== "" && !searching && favoriteHits.length === 0 && people.length === 0 && companies.length === 0;

  const pickFirst = () => {
    if (q) {
      const person = favoriteHits[0]?.login ?? people[0]?.login;
      if (person) selectPerson(person);
      else if (companies[0]) selectCompany(companies[0].customer_id);
    } else if (tab === "companies") {
      if (ownCompanies[0]) selectCompany(ownCompanies[0].customer_id);
    } else {
      const first = tab === "favorites" ? favorites[0] : (recent[0] ?? frequent[0]);
      if (first) selectPerson(first.login);
    }
  };
  const tabs: { key: RailTab; label: string; count?: number }[] = [
    { key: "favorites", label: t("customerWorkbench.favorites"), count: favorites.length },
    { key: "people", label: t("customerWorkbench.people") },
    { key: "companies", label: t("customerWorkbench.companies") },
  ];

  return (
    <div
      className="relative flex min-h-full flex-col"
      data-testid="customer-directory-page"
      {...drop.dropProps}
    >
      <DropOverlay active={drop.active}>{t("vcard.dropHere")}</DropOverlay>
      <CustomerCallStrip onShowCustomer={selectPerson} />
      <div className="flex flex-1 flex-wrap">
        <aside
          className="flex w-full flex-col gap-3 border-hairline bg-surface/40 px-3 py-4 lg:sticky lg:top-0 lg:max-h-[calc(100vh-3rem)] lg:w-[340px] lg:border-r"
          aria-label={t("customerDirectory.title")}
        >
          <div
            role="tablist"
            aria-label={t("customerWorkbench.scope")}
            className="flex gap-0.5 rounded-lg border border-hairline bg-bg p-0.5"
          >
            {tabs.map((tb) => {
              const on = !q && tab === tb.key;
              return (
                <button
                  key={tb.key}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  className={tabClass(on)}
                  onClick={() => pickTab(tb.key)}
                  data-testid={`customer-tab-${tb.key}`}
                >
                  {tb.key === "favorites" && (
                    <StarIcon className="text-[14px] text-warn" fill="currentColor" />
                  )}
                  {tb.label}
                  {tb.count != null && tb.count > 0 && (
                    <span className="text-[11px] tabular-nums text-muted">{tb.count}</span>
                  )}
                </button>
              );
            })}
          </div>

          <label className="relative block">
            <span className="sr-only">{t("customerWorkbench.searchLabel")}</span>
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[16px] text-muted" />
            <input
              type="search"
              value={query}
              autoFocus
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  pickFirst();
                }
              }}
              placeholder={t("customerWorkbench.searchAll")}
              data-testid="customer-directory-search"
              className="w-full rounded-lg border border-hairline bg-surface py-2.5 pl-9 pr-3 text-[14px] text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
            />
          </label>

          {q && <p className="-mt-1 px-1 text-xs text-muted">{t("customerWorkbench.searchScope")}</p>}

          <div className="-mx-1 min-h-0 flex-1 overflow-y-auto" data-testid="customer-rail-list">
            {!q && tab === "favorites" && (
              <>
                {favorites.map((e) => (
                  <PersonRow
                    key={`fav-${e.login}`}
                    customer={e}
                    sub={e.company_name || e.email}
                    favorite
                    selected={search.sel === e.login}
                    onSelect={() => selectPerson(e.login)}
                    testId={`customer-favorite-${e.login}`}
                  />
                ))}
                {shortlistQ.isLoading ? (
                  <RailSpinner />
                ) : (
                  favorites.length === 0 && <EmptyRail>{t("customerWorkbench.favoritesEmpty")}</EmptyRail>
                )}
              </>
            )}

            {!q && tab === "people" && (
              <ShortlistSections
                recent={recent}
                frequent={frequent}
                favoriteLogins={favoriteLogins}
                loading={shortlistQ.isLoading}
                selected={search.sel}
                onSelect={selectPerson}
                when={(iso) => shortWhen(iso, i18n.language)}
              />
            )}

            {!q && tab === "companies" && (
              <>
                <RailHeading>{t("customerWorkbench.ownCompanies")}</RailHeading>
                {ownCompanies.map((co) => (
                  <CompanyRow
                    key={co.customer_id}
                    name={co.name}
                    url={companyUrl(co)}
                    customerId={co.customer_id}
                    selected={search.company === co.customer_id && !search.sel}
                    onSelect={() => selectCompany(co.customer_id)}
                  />
                ))}
                {ownCompanies.length === 0 && (
                  <EmptyRail>{t("customerWorkbench.searchCompaniesHint")}</EmptyRail>
                )}
              </>
            )}

            {q && (
              <>
                {favoriteHits.length > 0 && (
                  <RailHeading count={favoriteHits.length}>{t("customerWorkbench.favorites")}</RailHeading>
                )}
                {favoriteHits.map((e) => (
                  <PersonRow
                    key={`fav-${e.login}`}
                    customer={e}
                    sub={e.company_name || e.email}
                    favorite
                    selected={search.sel === e.login}
                    onSelect={() => selectPerson(e.login)}
                    testId={`customer-favorite-${e.login}`}
                  />
                ))}
                {people.length > 0 && (
                  <RailHeading count={peopleTotal - (peopleQ.data?.items.length ?? 0) + people.length}>
                    {t("customerWorkbench.people")}
                  </RailHeading>
                )}
                {people.map((p) => (
                  <PersonRow
                    key={p.login}
                    customer={p}
                    sub={[p.company_name, p.email].filter(Boolean).join(", ")}
                    invalid={p.valid_id !== 1}
                    favorite={false}
                    selected={search.sel === p.login}
                    onSelect={() => selectPerson(p.login)}
                    testId={`customer-row-${p.login}`}
                  />
                ))}
                {(peopleQ.data?.items.length ?? 0) < peopleTotal && (
                  <button
                    type="button"
                    className="mx-2 mt-1 text-sm text-accent hover:underline"
                    onClick={() => setPageSize((n) => n + PAGE_SIZE)}
                    data-testid="customer-more"
                  >
                    {peopleQ.isFetching ? <Spinner className="h-3.5 w-3.5" /> : t("customerWorkbench.more")}
                  </button>
                )}
                {companies.length > 0 && (
                  <RailHeading count={companies.length}>{t("customerWorkbench.companies")}</RailHeading>
                )}
                {companies.map((co) => (
                  <CompanyRow
                    key={co.customer_id}
                    name={co.name}
                    url={companyUrl(co)}
                    customerId={co.customer_id}
                    selected={search.company === co.customer_id && !search.sel}
                    onSelect={() => selectCompany(co.customer_id)}
                  />
                ))}
                {searching && <RailSpinner />}
                {noHits && (
                  <EmptyRail>
                    {t("customerWorkbench.noHits")}
                    {canEdit && (
                      <button
                        type="button"
                        className="mt-2 block font-medium text-accent hover:underline"
                        onClick={() => setDrawer({ mode: "create", prefill: prefillFrom(q) })}
                        data-testid="customer-create-from-search"
                      >
                        {t("customerWorkbench.createFromSearch", { term: q })}
                      </button>
                    )}
                  </EmptyRail>
                )}
              </>
            )}
          </div>

          <div className="space-y-2 border-t border-hairline pt-3">
            {canEdit && (
              <button
                type="button"
                onClick={() => setDrawer({ mode: "create" })}
                className="flex w-full items-center justify-center gap-1.5 rounded-md border border-hairline bg-surface px-3 py-1.5 text-sm font-medium text-ink hover:bg-surface-subtle"
                data-testid="customer-new"
              >
                <PlusIcon className="text-[14px]" />
                {t("customerWorkbench.newCustomer")}
              </button>
            )}
            {canEdit && (
              <>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".vcf,text/vcard,text/x-vcard"
                  className="hidden"
                  data-testid="customer-import-file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (file) {
                      setImportError(null);
                      setImportFile(file);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => importInputRef.current?.click()}
                  title={t("vcard.importHint")}
                  className="flex w-full items-center justify-center gap-1.5 rounded-md border border-hairline bg-surface px-3 py-1.5 text-sm font-medium text-ink hover:bg-surface-subtle"
                  data-testid="customer-import"
                >
                  <UploadIcon className="text-[14px]" />
                  {t("vcard.import")}
                </button>
                {importError && (
                  <p className="px-1 text-xs text-danger" role="alert" data-testid="customer-import-rejected">
                    {importError}
                  </p>
                )}
              </>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-xs text-muted">
              <label className="inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={Boolean(search.invalid)}
                  onChange={(e) => setSearch({ invalid: e.target.checked || undefined })}
                  data-testid="customer-directory-invalid"
                />
                {t("customerDirectory.includeInvalid")}
              </label>
              {q && peopleTotal > 0 && peopleTotal <= VCARD_EXPORT_MAX && (
                <a
                  href={api.customerDirectoryVcardsUrl({ search: q, valid })}
                  download
                  className="inline-flex items-center gap-1 text-accent hover:underline"
                  data-testid="customer-directory-export-list"
                >
                  <DownloadIcon className="text-[13px]" />
                  {t("customerWorkbench.exportHits", { count: peopleTotal })}
                </a>
              )}
            </div>
          </div>
        </aside>

        <section className="min-w-0 flex-[999_1_460px] px-6 py-5" aria-live="polite">
          {search.sel ? (
            <CustomerPanel
              key={search.sel}
              login={search.sel}
              onEdit={(customer) => setDrawer({ mode: "edit", customer })}
              onOpenCompany={openCompany}
            />
          ) : search.company ? (
            <CompanyPanel
              key={search.company}
              customerId={search.company}
              onSelectContact={selectPerson}
              onAddContact={(company) => setDrawer({ mode: "create", company })}
              onVcard={(file) => {
                setImportError(null);
                setImportFile(file);
              }}
            />
          ) : (
            <EmptyPane canDrop={canEdit}>
              {tab === "companies" ? t("customerWorkbench.pickCompany") : t("customerWorkbench.pickCustomer")}
            </EmptyPane>
          )}
        </section>
      </div>

      {/* Hidden while the create form for one of its cards is open; it comes
          back afterwards (re-matched, so a just-created customer shows up). */}
      {importFile && !drawer && (
        <VcardImportDialog
          file={importFile}
          onClose={() => setImportFile(null)}
          onCreate={(prefill) => setDrawer({ mode: "create", prefill })}
        />
      )}

      {drawer && (
        <CustomerEditDrawer
          state={drawer}
          onClose={() => setDrawer(null)}
          onSaved={(login) => {
            setDrawer(null);
            selectPerson(login);
          }}
        />
      )}
    </div>
  );
}

/** A typed search term as create-form prefill: an address, a number or a name. */
function prefillFrom(term: string) {
  const v = term.trim();
  if (v.includes("@")) return { email: v };
  if (/^[+\d][\d\s/()-]{4,}$/.test(v)) return { phone: v };
  const parts = v.split(/\s+/);
  return parts.length > 1
    ? { first_name: parts.slice(0, -1).join(" "), last_name: parts[parts.length - 1] }
    : { last_name: v };
}

function ShortlistSections({
  recent,
  frequent,
  favoriteLogins,
  loading,
  selected,
  onSelect,
  when,
}: {
  recent: CustomerShortlistEntry[];
  frequent: CustomerShortlistEntry[];
  favoriteLogins: Set<string>;
  loading: boolean;
  selected?: string;
  onSelect: (login: string) => void;
  when: (iso: string) => string;
}) {
  const { t } = useTranslation();
  const channel = (name: string | null | undefined) =>
    name === "Email"
      ? t("customerWorkbench.viaEmail")
      : name === "Phone"
        ? t("customerWorkbench.viaPhone")
        : name === "Internal"
          ? t("customerWorkbench.viaNote")
          : t("customerWorkbench.viaOther");
  if (loading) return <RailSpinner />;
  if (recent.length === 0 && frequent.length === 0) {
    return <EmptyRail>{t("customerWorkbench.shortlistEmpty")}</EmptyRail>;
  }
  return (
    <>
      {recent.length > 0 && <RailHeading>{t("customerWorkbench.recent")}</RailHeading>}
      {recent.map((e) => (
        <PersonRow
          key={`r-${e.login}`}
          customer={e}
          sub={[e.company_name, channel(e.last_channel)].filter(Boolean).join(", ")}
          meta={when(e.last_at)}
          favorite={favoriteLogins.has(e.login)}
          selected={selected === e.login}
          onSelect={() => onSelect(e.login)}
          testId={`customer-recent-${e.login}`}
        />
      ))}
      {frequent.length > 0 && <RailHeading>{t("customerWorkbench.frequent")}</RailHeading>}
      {frequent.map((e) => (
        <PersonRow
          key={`f-${e.login}`}
          customer={e}
          sub={e.company_name || e.email}
          meta={t("customerWorkbench.ticketCount", { count: e.ticket_count })}
          favorite={favoriteLogins.has(e.login)}
          selected={selected === e.login}
          onSelect={() => onSelect(e.login)}
          testId={`customer-frequent-${e.login}`}
        />
      ))}
    </>
  );
}

function RailSpinner() {
  return (
    <div className="flex justify-center py-6">
      <Spinner className="h-4 w-4" />
    </div>
  );
}

function RailHeading({ children, count }: { children: ReactNode; count?: number }) {
  return (
    <h3 className="mx-2 mb-1 mt-3 flex justify-between text-xs font-semibold text-muted first:mt-1">
      {children}
      {count != null && <span className="font-normal tabular-nums">{count}</span>}
    </h3>
  );
}

function EmptyRail({ children }: { children: ReactNode }) {
  return <div className="px-2 py-4 text-sm text-muted">{children}</div>;
}

/** The right pane with nothing selected; for editors also where contacts can be dropped. */
function EmptyPane({ children, canDrop }: { children: ReactNode; canDrop: boolean }) {
  const { t } = useTranslation();
  return (
    <div
      className="flex min-h-[16rem] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-hairline p-8 text-center text-sm text-muted"
      data-testid="customer-empty-pane"
    >
      <p>{children}</p>
      {canDrop && (
        <p className="flex max-w-sm items-center gap-2 text-left text-xs">
          <AddressBookIcon className="shrink-0 text-[18px] text-accent" />
          {t("vcard.dropEmptyHint")}
        </p>
      )}
    </div>
  );
}

const rowClass = (selected: boolean) =>
  cn(
    "flex w-full min-w-0 flex-1 items-center gap-2.5 rounded-md px-2 py-1.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent",
    selected ? "bg-accent-dim shadow-[inset_3px_0_0_var(--color-accent)]" : "hover:bg-surface-subtle",
  );

/**
 * One person in the rail: the row opens them, the star beside it (and on the
 * avatar) says and toggles whether they are one of the agent's favorites.
 */
function PersonRow({
  customer,
  sub,
  meta,
  invalid,
  favorite,
  selected,
  onSelect,
  testId,
}: {
  customer: FavoriteCustomer;
  sub: string;
  meta?: string;
  invalid?: boolean;
  favorite: boolean;
  selected: boolean;
  onSelect: () => void;
  testId: string;
}) {
  const { t } = useTranslation();
  const toggleFavorite = useFavoriteToggle();
  const name = customerName(customer);
  const starLabel = favorite ? t("customerWorkbench.favoriteRemove") : t("customerWorkbench.favoriteAdd");
  return (
    <div className="group flex items-center gap-0.5">
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected || undefined}
        className={rowClass(selected)}
        data-testid={testId}
      >
        <span className="relative shrink-0">
          <Avatar email={customer.email} initials={initialsOf(name)} size={30} />
          {favorite && (
            <span className="absolute -bottom-1 -right-1 grid h-4 w-4 place-items-center rounded-full bg-bg text-warn">
              <StarIcon className="text-[11px]" fill="currentColor" />
              <span className="sr-only">{t("customerWorkbench.favorite")}</span>
            </span>
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className={cn("block truncate font-medium", invalid ? "text-muted line-through" : "text-ink")}>
            {name}
            {invalid && <span className="sr-only"> ({t("customerWorkbench.invalid")})</span>}
          </span>
          <span className="block truncate text-xs text-muted">{sub}</span>
        </span>
        {meta && <span className="shrink-0 text-[11px] tabular-nums text-muted">{meta}</span>}
      </button>
      <button
        type="button"
        onClick={() => toggleFavorite.mutate({ customer, favorite: !favorite })}
        aria-pressed={favorite}
        aria-label={`${starLabel}: ${name}`}
        title={starLabel}
        className={cn(
          "grid h-8 w-8 shrink-0 place-items-center rounded-md hover:bg-surface-subtle hover:text-warn focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent",
          favorite ? "text-warn" : "text-muted/60",
        )}
        data-testid={`customer-star-${customer.login}`}
      >
        <StarIcon className="text-[15px]" fill={favorite ? "currentColor" : "none"} />
      </button>
    </div>
  );
}

/** Website of a company search hit — only when the API sends one (the
 * shortlist-derived rows have none). */
function companyUrl(co: object): string | null {
  return "url" in co && typeof co.url === "string" ? co.url : null;
}

function CompanyRow({
  name,
  url,
  customerId,
  selected,
  onSelect,
}: {
  name: string;
  url?: string | null;
  customerId: string;
  selected: boolean;
  onSelect: () => void;
}) {
  const domain = domainFromUrl(url);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected || undefined}
      className={rowClass(selected)}
      data-testid={`company-row-${customerId}`}
    >
      {domain ? (
        <Avatar
          domain={domain}
          initials={initialsOf(name)}
          size={30}
          testId={`company-row-logo-${customerId}`}
        />
      ) : (
        <span className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-md border border-hairline bg-surface-subtle text-muted">
          <BuildingIcon className="text-[15px]" />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-ink">{name}</span>
        <span className="block truncate font-mono text-[11px] text-muted">{customerId}</span>
      </span>
    </button>
  );
}
